import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPhoneNumber } from "@/lib/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown } from "lucide-react";
import { useMemo } from "react";
import { type WorkerModel } from "../../../../firestore_types";
import { useAdminWorkers } from "../-admin-context";

export const Route = createFileRoute("/dashboard/admin/debugWorkers/")({
  component: RouteComponent,
});

function RouteComponent() {
  const workersResource = useAdminWorkers();

  const loading = workersResource.tag === "loading";
  const workers = workersResource.tag === "success" ? workersResource.asData : [];

  const columns = useMemo<ColumnDef<WorkerModel>[]>(
    () => [
      {
        accessorKey: "name",
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Worker Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const worker = row.original;
          return (
            <div className="space-y-1">
              <Link
                to="/dashboard/admin/debugWorkers/$workerId"
                params={{ workerId: worker.id }}
                className="text-primary font-medium hover:underline"
              >
                {worker.name}
              </Link>
              <div className="text-muted-foreground text-xs">{worker.id}</div>
            </div>
          );
        },
      },
      {
        accessorKey: "workerCode",
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Worker Code
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const workerCode = row.original.workerCode;
          return workerCode ? (
            <code className="rounded bg-gray-100 px-2 py-1 text-sm">{workerCode}</code>
          ) : (
            <span className="text-muted-foreground text-xs italic">No code</span>
          );
        },
      },
      {
        accessorKey: "phoneNumber",
        header: "Phone Number",
        cell: ({ row }) => (
          <div className="text-sm">{formatPhoneNumber(row.original.phoneNumber)}</div>
        ),
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => {
          const email = row.original.email;
          return email ? (
            <div className="text-sm">{email}</div>
          ) : (
            <span className="text-muted-foreground text-xs italic">-</span>
          );
        },
      },
    ],
    [],
  );

  if (loading) {
    return (
      <div className="p-1 sm:p-6">
        <h1 className="mb-6 text-2xl font-bold">Debug Workers</h1>
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1 sm:p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Debug Workers</h1>
        <p className="text-muted-foreground text-sm">
          Click on a worker to debug their work unit assignments
        </p>
      </div>

      <DataTable<WorkerModel, unknown>
        columns={columns}
        data={workers}
        isLoading={loading}
        disablePagination={true}
        emptyMessage="No workers found in the system."
      />
    </div>
  );
}
